import { useCallback, useEffect, useState } from 'react';
import { setStorageItem } from '../utils/storage';
import { ReadingHistoryRecordSchema } from '../utils/validation/storageSchemas';

export interface ReadingHistoryItem {
  novelId: string;
  sectionIndex: number;
  sectionTitle: string;
  progressPercentage: number;
  updatedAt: number;
}

const HISTORY_KEY = 'novelflow_reading_history';
let globalHistoryCache: Record<string, ReadingHistoryItem> | null = null;
const listeners = new Set<(h: Record<string, ReadingHistoryItem>) => void>();

const getStoredHistory = (): Record<string, ReadingHistoryItem> => {
  if (globalHistoryCache) return globalHistoryCache;
  try {
    const saved = localStorage.getItem(HISTORY_KEY);
    if (!saved) return {};
    const parsed = JSON.parse(saved);
    const result = ReadingHistoryRecordSchema.safeParse(parsed);
    if (result.success) {
      globalHistoryCache = result.data;
      return globalHistoryCache;
    }
    console.warn('Failed to parse reading history:', result.error);
    return {};
  } catch {
    return {};
  }
};

let saveTimeout: ReturnType<typeof setTimeout> | null = null;

const persistHistoryToStorage = (history: Record<string, ReadingHistoryItem>) => {
  setStorageItem(HISTORY_KEY, JSON.stringify(history));
};

const asyncSaveToStorage = (history: Record<string, ReadingHistoryItem>) => {
  if (saveTimeout) {
    clearTimeout(saveTimeout);
  }

  saveTimeout = setTimeout(() => {
    persistHistoryToStorage(history);
    saveTimeout = null;
  }, 100); // 100msのデバウンス
};

const flushHistoryToStorage = () => {
  if (saveTimeout) {
    clearTimeout(saveTimeout);
    saveTimeout = null;
  }
  persistHistoryToStorage(getStoredHistory());
};

const updateStoredHistory = (newHistory: Record<string, ReadingHistoryItem>) => {
  globalHistoryCache = newHistory;
  // リスナーへの通知は同期的におこないUIをすぐに更新する
  for (const l of listeners) {
    l(newHistory);
  }
  // localStorageへの書き込み(JSON.stringify)を非同期/デバウンス化
  asyncSaveToStorage(newHistory);
};

export const useReadingHistory = () => {
  const [history, setHistory] = useState<Record<string, ReadingHistoryItem>>(() =>
    getStoredHistory(),
  );

  useEffect(() => {
    const listener = (newHistory: Record<string, ReadingHistoryItem>) => {
      setHistory(newHistory);
    };
    listeners.add(listener);

    // タブ間での同期用（別ウィンドウ/タブで更新された際にも同期）
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === HISTORY_KEY) {
        globalHistoryCache = null;
        const updated = getStoredHistory();
        for (const l of listeners) {
          l(updated);
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      listeners.delete(listener);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const saveHistoryItem = useCallback(
    (novelId: string, item: Omit<ReadingHistoryItem, 'novelId' | 'updatedAt'>) => {
      const current = getStoredHistory();
      const updated = {
        ...current,
        [novelId]: {
          novelId,
          ...item,
          updatedAt: Date.now(),
        },
      };
      updateStoredHistory(updated);
    },
    [],
  );

  const deleteHistoryItem = useCallback((novelId: string) => {
    const current = getStoredHistory();
    const updated = { ...current };
    delete updated[novelId];
    updateStoredHistory(updated);
  }, []);

  const reloadHistory = useCallback(() => {
    globalHistoryCache = null;
    const updated = getStoredHistory();
    for (const l of listeners) {
      l(updated);
    }
  }, []);

  const flushHistory = useCallback(() => {
    flushHistoryToStorage();
  }, []);

  useEffect(() => {
    const handlePageHide = () => {
      flushHistoryToStorage();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flushHistoryToStorage();
      }
    };

    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return {
    history,
    saveHistoryItem,
    deleteHistoryItem,
    reloadHistory,
    flushHistory,
  };
};
